<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import inboxesAPI from 'dashboard/api/inboxes';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

// Keep in sync with Whatsapp::SyncEvolutionContactsService::{DEFAULT_CHATS,MAX_CHATS}.
const DEFAULT_LIMIT = 100;
const MAX_LIMIT = 500;

const { t } = useI18n();
const dialogRef = ref(null);
const isSyncing = ref(false);
const limit = ref(DEFAULT_LIMIT);

const open = () => {
  limit.value = DEFAULT_LIMIT;
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

// Fire-and-forget: the import runs in a background job (Whatsapp::SyncEvolutionContactsService),
// so there's nothing to await beyond enqueueing it - contacts show up as they're created.
const handleConfirm = async () => {
  if (isSyncing.value) return;

  isSyncing.value = true;
  try {
    await inboxesAPI.syncEvolutionContacts(props.inboxId, limit.value);
    useAlert(t('CHAT_LIST.SYNC_EVOLUTION_CONTACTS_DIALOG.SUCCESS'));
    close();
  } catch (error) {
    useAlert(t('CHAT_LIST.SYNC_EVOLUTION_CONTACTS_DIALOG.ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CHAT_LIST.SYNC_EVOLUTION_CONTACTS_DIALOG.TITLE')"
    :description="
      t('CHAT_LIST.SYNC_EVOLUTION_CONTACTS_DIALOG.DESCRIPTION', {
        max: MAX_LIMIT,
      })
    "
    :confirm-button-label="t('CHAT_LIST.SYNC_EVOLUTION_CONTACTS_DIALOG.SUBMIT')"
    :is-loading="isSyncing"
    @confirm="handleConfirm"
  >
    <Input
      v-model="limit"
      type="number"
      min="1"
      :max="String(MAX_LIMIT)"
      :label="t('CHAT_LIST.SYNC_EVOLUTION_CONTACTS_DIALOG.LIMIT_LABEL')"
    />
  </Dialog>
</template>
